import { useState } from "react";
import * as api from "../lib/api";
import { useStore } from "../lib/store";
import type { Candidate, ExtractResponse, SourceKind } from "../lib/types";
import CandidateList from "./CandidateList";

type Stage = "form" | "working" | "review" | "saved";

export default function Ingest() {
  const { lang, syncNow } = useStore();
  const [stage, setStage] = useState<Stage>("form");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [episodeDate, setEpisodeDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [kind, setKind] = useState<SourceKind>("podcast");
  const [focus, setFocus] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ExtractResponse | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [savedCount, setSavedCount] = useState(0);

  async function runExtraction() {
    setError(null);
    setStage("working");
    try {
      const response = await api.extract({
        lang,
        url: url.trim() || undefined,
        text: text.trim() || undefined,
        title: title.trim() || undefined,
        sourceKind: kind,
        episodeDate,
        focus: focus.trim() || undefined,
      });
      setResult(response);
      setCandidates(response.candidates);
      // Everything starts selected: rejecting the few duds is faster than
      // picking twenty keepers out of a list.
      setSelected(new Set(response.candidates.map((_, i) => i)));
      if (!title.trim()) setTitle(response.sourceTitle);
      setStage("review");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStage("form");
    }
  }

  async function save() {
    if (!result) return;
    setStage("working");
    setError(null);
    try {
      const keep = candidates.filter((_, i) => selected.has(i));
      const response = await api.approve({
        lang,
        source: {
          kind,
          title: title.trim() || result.sourceTitle,
          url: result.sourceUrl,
          episodeDate,
          body: result.body,
        },
        candidates: keep,
      });
      setSavedCount(response.notes.length);
      await syncNow();
      setStage("saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStage("review");
    }
  }

  function reset() {
    setStage("form");
    setUrl("");
    setText("");
    setTitle("");
    setFocus("");
    setResult(null);
    setCandidates([]);
    setSelected(new Set());
    setError(null);
  }

  if (stage === "saved") {
    return (
      <div className="empty">
        <div className="big">✓</div>
        <p>{savedCount} card{savedCount === 1 ? "" : "s"} added to your {lang === "fr" ? "French" : "Spanish"} collection.</p>
        <button className="btn primary" onClick={reset}>Add another source</button>
      </div>
    );
  }

  if (stage === "review" && result) {
    return (
      <>
        <h1>Approve</h1>
        <p className="lede">
          {candidates.length} candidate{candidates.length === 1 ? "" : "s"} from “{result.sourceTitle}”.
          {result.duplicates.length > 0 &&
            ` ${result.duplicates.length} you already have ${result.duplicates.length === 1 ? "was" : "were"} skipped.`}
        </p>

        {error && <div className="notice error" style={{ marginBottom: 12 }}>{error}</div>}

        <div className="stack">
          <label className="field">
            Source title
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
        </div>

        <div style={{ height: 12 }} />

        <CandidateList
          candidates={candidates}
          selected={selected}
          onToggle={(i) => {
            setSelected((current) => {
              const next = new Set(current);
              if (next.has(i)) next.delete(i);
              else next.add(i);
              return next;
            });
          }}
          onEdit={(i, patch) => {
            setCandidates((current) => current.map((c, j) => (j === i ? { ...c, ...patch } : c)));
          }}
        />

        <div className="stickybar">
          <div className="row">
            <button className="btn ghost" onClick={reset}>Discard</button>
            <button className="btn primary" style={{ flex: 1 }} disabled={selected.size === 0} onClick={() => void save()}>
              Add {selected.size} card{selected.size === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <h1>Add a source</h1>
      <p className="lede">
        Paste a link to the episode page or transcript. If the page builds its transcript
        with JavaScript, paste the text instead.
      </p>

      {error && <div className="notice error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="stack">
        <label className="field">
          Link
          <input
            type="url"
            inputMode="url"
            placeholder="https://…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={stage === "working"}
          />
        </label>

        <label className="field">
          …or paste the text
          <textarea
            placeholder="Transcript, article, or document text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={stage === "working"}
          />
        </label>

        <div className="row">
          <label className="field" style={{ flex: 1 }}>
            Type
            <select value={kind} onChange={(e) => setKind(e.target.value as SourceKind)}>
              <option value="podcast">Podcast episode</option>
              <option value="document">Document</option>
              <option value="manual">Other</option>
            </select>
          </label>
          <label className="field" style={{ flex: 1 }}>
            Date
            <input type="date" value={episodeDate} onChange={(e) => setEpisodeDate(e.target.value)} />
          </label>
        </div>

        <label className="field">
          Focus (optional)
          <input
            type="text"
            placeholder="e.g. the section on tailings management"
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
          />
        </label>

        <button
          className="btn primary full"
          disabled={stage === "working" || (!url.trim() && !text.trim())}
          onClick={() => void runExtraction()}
        >
          {stage === "working" ? <><span className="spinner" /> Reading…</> : "Extract cards"}
        </button>
      </div>
    </>
  );
}
